import { useEffect, useId, useMemo, useRef, useState } from 'react';
import { useTranslation } from '../../../i18n/useTranslation';
import { Button } from '../../atoms/Button/Button';
import { Checkbox } from '../../atoms/Checkbox';
import { EmptyState } from '../../atoms/EmptyState';
import { Icon } from '../../atoms/Icon';
import { Inline } from '../../atoms/Inline/Inline';
import { Stack } from '../../atoms/Stack/Stack';
import { Text } from '../../atoms/Typography';
import './ComparisonPhotoPicker.css';
import { LiveRegion } from '../../atoms/LiveRegion';
import { LoadErrorState } from '../../feedback/LoadErrorState/LoadErrorState';

/** One gallery photo offered by the "choose photos" mode. */
export interface ComparisonPhotoOption {
  id: string;
  /** ISO 8601 photo date (photoTakenAt, else upload date) — used for ordering. */
  takenAt: string;
  /** Thumbnail URL. */
  src: string;
  /** Pre-formatted, locale-aware photo date. */
  date: string;
}

/** A selected photo: its id and photo date (kept even when its page is not loaded). */
export interface ComparisonPickedPhoto {
  id: string;
  takenAt: string;
}

export type ComparisonPhotoPickerState = 'loading' | 'error' | 'empty' | 'ready';

export interface ComparisonPhotoPickerProps {
  state: ComparisonPhotoPickerState;
  /** Loaded gallery photos, in display order. */
  photos: ComparisonPhotoOption[];
  /** Currently selected photos. */
  selected: ComparisonPickedPhoto[];
  /** Selection limits (backend: MIN/MAX_COMPARISON_IMAGES). */
  min?: number;
  max?: number;
  /** Disables the selection while a comparison runs. */
  disabled?: boolean;
  /** Adds (or, if already selected, removes) a photo. Never called for a selection beyond `max`. */
  onToggle: (photo: ComparisonPickedPhoto) => void;
  hasMore?: boolean;
  isLoadingMore?: boolean;
  onLoadMore?: () => void;
  /** Reloads the gallery after a failed load. */
  onRetry: () => void;
  /** Opens the aquarium gallery (empty-state action). */
  onOpenGallery: () => void;
}

function photoTime(iso: string): number {
  const ms = Date.parse(iso);
  return Number.isNaN(ms) ? Number.POSITIVE_INFINITY : ms;
}

/**
 * ComparisonPhotoPicker — the "choose photos" mode of the comparison screen: the
 * aquarium's gallery as a grid of checkboxes (native keyboard + ARIA semantics),
 * a live selection counter, and the min/max rules. A selected photo shows a
 * check icon plus "Selected (no. n)" text — n is its position by photo date, the
 * order the comparison will use — so the state never relies on colour alone.
 */
export function ComparisonPhotoPicker({
  state,
  photos,
  selected,
  min = 2,
  max = 5,
  disabled = false,
  onToggle,
  hasMore = false,
  isLoadingMore = false,
  onLoadMore,
  onRetry,
  onOpenGallery,
}: ComparisonPhotoPickerProps) {
  const { t } = useTranslation();
  const baseId = useId();
  const [limitReached, setLimitReached] = useState(false);
  const loadMoreRef = useRef<HTMLButtonElement>(null);
  const listRef = useRef<HTMLUListElement>(null);
  /** Photo count when "Load more" was pressed; null when no load is pending. */
  const loadMoreFrom = useRef<number | null>(null);

  // Position of each selected photo by photo date (1 = earliest).
  const positions = useMemo(() => {
    const ordered = [...selected].sort(
      (a, b) => photoTime(a.takenAt) - photoTime(b.takenAt) || (a.id < b.id ? -1 : 1),
    );
    return new Map(ordered.map((photo, index) => [photo.id, index + 1]));
  }, [selected]);

  // The limit message only applies while the selection is full.
  useEffect(() => {
    if (selected.length < max) setLimitReached(false);
  }, [selected.length, max]);

  // After "Load more", continue at the first newly loaded photo (or the button).
  useEffect(() => {
    const from = loadMoreFrom.current;
    if (from === null || isLoadingMore) return;
    loadMoreFrom.current = null;
    const inputs = listRef.current?.querySelectorAll<HTMLInputElement>('input[type="checkbox"]');
    const firstNew = inputs && inputs.length > from ? inputs[from] : undefined;
    (firstNew ?? loadMoreRef.current)?.focus();
  }, [isLoadingMore, photos.length]);

  const handleToggle = (photo: ComparisonPhotoOption) => {
    const isSelected = positions.has(photo.id);
    if (!isSelected && selected.length >= max) {
      setLimitReached(true);
      return;
    }
    setLimitReached(false);
    onToggle({ id: photo.id, takenAt: photo.takenAt });
  };

  const handleLoadMore = () => {
    if (isLoadingMore || !onLoadMore) return;
    loadMoreFrom.current = photos.length;
    onLoadMore();
  };

  if (state === 'loading') {
    return (
      <div className="comparison-photo-picker" aria-busy="true">
        <Stack gap="3">
          <LiveRegion>
            <Text variant="muted">{t('comparison.photos.loading')}</Text>
          </LiveRegion>
          <div className="comparison-photo-picker__grid" aria-hidden="true">
            {[0, 1, 2, 3].map(n => (
              <div key={n} className="comparison-photo-picker__skeleton" />
            ))}
          </div>
        </Stack>
      </div>
    );
  }

  if (state === 'error') {
    return (
      <LoadErrorState
        className="comparison-photo-picker"
        title={t('comparison.photos.error.title')}
        titleLevel={3}
        message={t('comparison.photos.error.description')}
        onRetry={onRetry}
        retryLabel={t('comparison.photos.error.retry')}
      />
    );
  }

  if (state === 'empty') {
    return (
      <EmptyState
        className="comparison-photo-picker"
        icon={<Icon name="image" size="lg" />}
        headline={t('comparison.photos.empty.title')}
        description={t('comparison.photos.empty.description')}
        headingLevel={3}
        actionLabel={t('comparison.photos.empty.action')}
        onAction={onOpenGallery}
        actionVariant="secondary"
      />
    );
  }

  const statusId = `${baseId}-status`;

  return (
    <div className="comparison-photo-picker">
      <Stack gap="3">
        <div id={statusId} aria-live="polite" className="comparison-photo-picker__status">
          <Text weight="semibold">
            {t('comparison.photos.counter', { selected: selected.length, max })}
          </Text>
          {selected.length < min && (
            <Text variant="muted">{t('comparison.photos.hintMin', { min })}</Text>
          )}
          {limitReached && (
            <Text className="comparison-photo-picker__limit">
              {t('comparison.photos.limitReached', { max })}
            </Text>
          )}
        </div>
        <Text variant="muted">{t('comparison.photos.orderHint')}</Text>
        <ul
          ref={listRef}
          className="comparison-photo-picker__grid"
          aria-label={t('comparison.photos.groupLabel')}
        >
          {photos.map(photo => {
            const position = positions.get(photo.id);
            const isSelected = position !== undefined;
            return (
              <li
                key={photo.id}
                className={[
                  'comparison-photo-picker__tile',
                  isSelected ? 'comparison-photo-picker__tile--selected' : '',
                ]
                  .filter(Boolean)
                  .join(' ')}
              >
                <Checkbox
                  id={`${baseId}-photo-${photo.id}`}
                  className="comparison-photo-picker__option"
                  checked={isSelected}
                  disabled={disabled}
                  aria-describedby={statusId}
                  onChange={() => handleToggle(photo)}
                  label={
                    <span className="comparison-photo-picker__body">
                      {photo.src ? (
                        <img className="comparison-photo-picker__thumb" src={photo.src} alt="" loading="lazy" />
                      ) : (
                        <span className="comparison-photo-picker__thumb comparison-photo-picker__thumb--missing" />
                      )}
                      <span className="comparison-photo-picker__date">
                        {t('comparison.photos.photoLabel', { date: photo.date })}
                      </span>
                      {isSelected && (
                        <span className="comparison-photo-picker__selected">
                          <Icon name="check" size="sm" />
                          {t('comparison.photos.selected', { position })}
                        </span>
                      )}
                    </span>
                  }
                />
              </li>
            );
          })}
        </ul>
        {hasMore && onLoadMore && (
          <Inline gap="2">
            <Button
              ref={loadMoreRef}
              variant="secondary"
              disabled={isLoadingMore}
              aria-busy={isLoadingMore || undefined}
              onClick={handleLoadMore}
            >
              {isLoadingMore ? t('comparison.photos.loadingMore') : t('comparison.photos.loadMore')}
            </Button>
          </Inline>
        )}
      </Stack>
    </div>
  );
}

export default ComparisonPhotoPicker;
